/**
 * ErrorBoundary — catches errors in child components and renders a fallback
 * UI instead of a blank screen.
 *
 * React class component because hooks don't have an equivalent for
 * componentDidCatch / getDerivedStateFromError.
 *
 * Used to wrap each major route (or the entire app) so that one buggy
 * component doesn't take down the whole UI.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { looksLikeChunkError, reloadForChunkError } from '@/lib/chunkReload';

interface Props {
  children: ReactNode;
  /** Optional fallback override. If not provided, the default fallback is shown. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  /** Called when an error is caught — useful for logging. */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface State {
  error: Error | null;
  isChunkError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, isChunkError: false };

  static getDerivedStateFromError(error: Error): State {
    return { error, isChunkError: looksLikeChunkError(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // A stale-deploy chunk failure: try to auto-recover with a single reload
    // (rate-limited to avoid loops). If a reload was triggered, the page is
    // already navigating away, so the fallback below is never seen.
    if (looksLikeChunkError(error)) {
      reloadForChunkError();
      return;
    }
    // Log to the console so devs can see the stack
    console.error('[ProjexHQ] Uncaught render error:', error);
    console.error('Component stack:', info.componentStack);
    this.props.onError?.(error, info);
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error, isChunkError } = this.state;
    if (error === null) return this.props.children;

    if (this.props.fallback) {
      return this.props.fallback(error, this.reset);
    }

    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <div className="max-w-lg w-full bg-white border border-line rounded-lg shadow-sm p-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-danger-bg flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-danger" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-bold text-ink-primary mb-1">
                {isChunkError ? 'A newer version is available' : 'Something went wrong'}
              </h2>
              <p className="text-sm text-ink-secondary mb-4">
                {isChunkError
                  ? 'This page couldn’t finish loading because the app was updated since you opened it. Reload to pick up the latest version.'
                  : 'The app hit an unexpected error rendering this page. The error has been logged to the browser console.'}
              </p>
              {!isChunkError && (
                <details className="mb-4">
                  <summary className="text-xs text-ink-tertiary cursor-pointer hover:text-ink-secondary">
                    Show error details
                  </summary>
                  <pre className="mt-2 p-3 bg-surface-muted rounded text-xs text-ink-secondary overflow-auto max-h-48 whitespace-pre-wrap break-words">
                    {error.message}
                    {error.stack ? `\n\n${error.stack}` : ''}
                  </pre>
                </details>
              )}
              <div className="flex flex-wrap gap-2">
                {isChunkError ? (
                  <Button variant="accent" onClick={() => window.location.reload()}>
                    <RefreshCw className="w-4 h-4 mr-1.5" />
                    Reload
                  </Button>
                ) : (
                  <Button variant="accent" onClick={this.reset}>
                    <RefreshCw className="w-4 h-4 mr-1.5" />
                    Try again
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => {
                    window.location.href = '/';
                  }}
                >
                  <Home className="w-4 h-4 mr-1.5" />
                  Go home
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
